/**
 * Universal error-payload formatter.
 *
 * Accepts whatever shape an upstream API returns — JSON object, list, string,
 * HTML document, or nothing — and produces a single human-readable line that
 * is safe to surface in the UI.
 *
 * Handles:
 *  - Plain strings (incl. accidental HTML responses → friendly fallback)
 *  - DRF serializer errors: { field: ["msg"], nested: { field: ["msg"] } }
 *  - Common envelopes: { error }, { detail }, { message }, { description },
 *                      { non_field_errors }, { errors }
 *  - Arrays of strings or { message } objects
 *  - { error: { ...fieldErrors } } where the value is itself a nested object
 */

const MAX_MESSAGE_LENGTH = 240;
const NETWORK_FALLBACK = 'Network error — please check your connection and try again.';
const GENERIC_FALLBACK = 'Something went wrong. Please try again.';

const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  non_field_errors: '',
  detail: '',
  message: '',
  error: '',
  description: '',
};

function humanizeFieldName(name: string): string {
  if (name in FIELD_LABEL_OVERRIDES) return FIELD_LABEL_OVERRIDES[name];
  return name
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function looksLikeHtml(s: string): boolean {
  const trimmed = s.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('<!') || trimmed.startsWith('<html') || trimmed.startsWith('<HTML')) {
    return true;
  }
  return /<\/?(html|body|head|div|p|h1|h2|title|script|style)[\s>]/i.test(trimmed);
}

function extractHtmlTitle(s: string): string | null {
  const m = s.match(/<title[^>]*>([^<]+)<\/title>/i);
  return m ? m[1].trim() : null;
}

function truncate(s: string): string {
  if (s.length <= MAX_MESSAGE_LENGTH) return s;
  return `${s.slice(0, MAX_MESSAGE_LENGTH - 1).trimEnd()}…`;
}

/**
 * Recursively walks a parsed error payload and yields message strings.
 * Field names become prefixes ("Email: Already in use") unless the key is
 * a sentinel envelope key like 'detail' or 'non_field_errors'.
 */
function* walkPayload(
  node: unknown,
  fieldPath: string[],
): Generator<string, void, unknown> {
  if (node === null || node === undefined) return;

  if (typeof node === 'string') {
    if (!node.trim()) return;
    if (looksLikeHtml(node)) {
      const title = extractHtmlTitle(node);
      yield title ? `Server error: ${title}` : 'Server returned an unexpected response.';
      return;
    }
    yield prefixWithField(node, fieldPath);
    return;
  }

  if (typeof node === 'number' || typeof node === 'boolean') {
    yield prefixWithField(String(node), fieldPath);
    return;
  }

  if (Array.isArray(node)) {
    for (const item of node) yield* walkPayload(item, fieldPath);
    return;
  }

  if (typeof node === 'object') {
    const obj = node as Record<string, unknown>;
    for (const [key, value] of Object.entries(obj)) {
      yield* walkPayload(value, [...fieldPath, key]);
    }
  }
}

function prefixWithField(message: string, fieldPath: string[]): string {
  const meaningful = fieldPath.filter((k) => !(k in FIELD_LABEL_OVERRIDES));
  if (meaningful.length === 0) return message;
  const label = meaningful.map(humanizeFieldName).join(' / ');
  if (!label) return message;
  if (message.toLowerCase().startsWith(label.toLowerCase())) return message;
  return `${label}: ${message}`;
}

export interface FormattedError {
  /** Single-line summary, safe to render anywhere. */
  message: string;
  /** All discrete messages found in the payload (useful for forms). */
  messages: string[];
  /** Best-effort field error map for form integration. */
  fieldErrors: Record<string, string>;
}

export function formatApiError(payload: unknown): FormattedError {
  if (payload === null || payload === undefined || payload === '') {
    return { message: GENERIC_FALLBACK, messages: [], fieldErrors: {} };
  }

  const messages: string[] = [];
  for (const m of walkPayload(payload, [])) {
    const cleaned = m.replace(/\s+/g, ' ').trim();
    if (cleaned) messages.push(cleaned);
  }

  const fieldErrors = collectFieldErrors(payload);

  const message = messages.length > 0
    ? truncate(messages.join(' '))
    : GENERIC_FALLBACK;

  return { message, messages, fieldErrors };
}

function collectFieldErrors(payload: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!payload || typeof payload !== 'object') return out;

  const root = (payload as Record<string, unknown>).error ?? payload;
  if (!root || typeof root !== 'object' || Array.isArray(root)) return out;

  for (const [key, value] of Object.entries(root as Record<string, unknown>)) {
    if (key in FIELD_LABEL_OVERRIDES) continue;
    const msgs: string[] = [];
    for (const m of walkPayload(value, [])) msgs.push(m);
    if (msgs.length > 0) out[key] = msgs[0];
  }

  return out;
}

/**
 * Format a thrown error (network, abort, server) into a UI-safe message.
 * Use this when you have a caught exception rather than just the payload.
 */
export function formatThrownError(err: unknown): string {
  if (err && typeof err === 'object') {
    const e = err as { name?: string; message?: string; status?: number; response?: unknown };

    if (e.name === 'AbortError') return 'Request was cancelled.';

    if (e.response !== undefined) {
      const formatted = formatApiError(e.response);
      if (formatted.message && formatted.message !== GENERIC_FALLBACK) {
        return formatted.message;
      }
    }

    if (typeof e.message === 'string' && e.message.trim()) {
      if (looksLikeHtml(e.message)) {
        const title = extractHtmlTitle(e.message);
        return title ? `Server error: ${title}` : 'Server returned an unexpected response.';
      }
      if (/^failed to fetch$|^networkerror/i.test(e.message)) return NETWORK_FALLBACK;
      return truncate(e.message);
    }
  }
  if (typeof err === 'string' && err.trim()) {
    return looksLikeHtml(err) ? 'Server returned an unexpected response.' : truncate(err);
  }
  return GENERIC_FALLBACK;
}
